import { put, call, takeLatest } from "redux-saga/effects";
import axios from 'axios';
import { HubAuthManager } from 'hub-redux-auth';

import API_ENDPOINTS from "@services/apiConfig";

/********** ACTION_TYPES **********/
export const actionTypes = {
    getToken: "hostedEsign/GET_TOKEN",
    getTokenFailed: "hostedEsign/GET_TOKEN_FAILED",
    getTokenFulfilled: "hostedEsign/GET_TOKEN_FULFILLED",

    sendAuthOtp: "hostedEsign/SEND_AUTH_OTP",
    sendAuthOtpFailed: "hostedEsign/SEND_AUTH_OTP_FAILED",
    sendAuthOtpFulfilled: "hostedEsign/SEND_AUTH_OTP_FULFILLED",

    verifyAuthOtp: "hostedEsign/VERIFY_AUTH_OTP",
    verifyAuthOtpFailed: "hostedEsign/VERIFY_AUTH_OTP_FAILED",
    verifyAuthOtpFulfilled: "hostedEsign/VERIFY_AUTH_OTP_FULFILLED",

    resetState: "hostedEsign/RESET_STATE",
};

/********** REDUCER **********/
const initialState = {
    token: {
        fetching: false,
        fetched: false,
        access_token: null,
        errMsg: null,
    },
    authOtp: {
        sending: false,
        sent: false,
        email: null,
        errMsg: null,
    },
    authVerify: {
        verifying: false,
        verified: false,
        merchant_token_response: null,
        errMsg: null,
    },
};

export const reducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.getToken: {
            return {
                ...state,
                token: {
                    ...state.token,
                    fetching: true,
                    errMsg: null,
                },
            };
        }
        case actionTypes.getTokenFailed: {
            return {
                ...state,
                token: {
                    ...state.token,
                    fetching: false,
                    fetched: true,
                    errMsg: action.payload,
                },
            };
        }
        case actionTypes.getTokenFulfilled: {
            return {
                ...state,
                token: {
                    ...state.token,
                    fetching: false,
                    fetched: true,
                    access_token: action.payload,
                    errMsg: null,
                },
            };
        }
        case actionTypes.sendAuthOtp: {
            return {
                ...state,
                authOtp: {
                    ...state.authOtp,
                    sending: true,
                    errMsg: null,
                },
            };
        }
        case actionTypes.sendAuthOtpFailed: {
            return {
                ...state,
                authOtp: {
                    ...state.authOtp,
                    sending: false,
                    sent: false,
                    errMsg: action.payload,
                },
            };
        }
        case actionTypes.sendAuthOtpFulfilled: {
            return {
                ...state,
                authOtp: {
                    ...state.authOtp,
                    sending: false,
                    sent: true,
                    email: action.payload?.email,
                    errMsg: null,
                },
            };
        }
        case actionTypes.verifyAuthOtp: {
            return {
                ...state,
                authVerify: {
                    ...state.authVerify,
                    verifying: true,
                    errMsg: null,
                },
            };
        }
        case actionTypes.verifyAuthOtpFailed: {
            return {
                ...state,
                authVerify: {
                    ...state.authVerify,
                    verifying: false,
                    verified: false,
                    errMsg: action.payload,
                },
            };
        }
        case actionTypes.verifyAuthOtpFulfilled: {
            return {
                ...state,
                authVerify: {
                    ...state.authVerify,
                    verifying: false,
                    verified: true,
                    merchant_token_response: action.payload?.token,
                    errMsg: null,
                },
            };
        }
        case actionTypes.resetState: {
            return initialState;
        }
        default: {
            return state;
        }
    }
};

/********** SELECTORS **********/
export const selectors = {
    tokenFetching: (state) => state.partnerApp?.hostedEsign?.token?.fetching || false,
    tokenAccessToken: (state) => state.partnerApp?.hostedEsign?.token?.access_token || null,
    tokenErrMsg: (state) => state.partnerApp?.hostedEsign?.token?.errMsg || null,

    authOtpSending: (state) => state.partnerApp?.hostedEsign?.authOtp?.sending || false,
    authOtpSent: (state) => state.partnerApp?.hostedEsign?.authOtp?.sent || false,
    authOtpErrMsg: (state) => state.partnerApp?.hostedEsign?.authOtp?.errMsg || null,
    authEmail: (state) => state.partnerApp?.hostedEsign?.authOtp?.email ||null,

    authVerifyVerifying: (state) => state.partnerApp?.hostedEsign?.authVerify?.verifying || false,
    authVerifyVerified: (state) => state.partnerApp?.hostedEsign?.authVerify?.verified || false,
    // authVerifyVerified: (state) => true,
    authVerifyAccessToken: (state) => state.partnerApp?.hostedEsign?.authVerify?.merchant_token_response?.access_token || null,
    authVerifyTokenType: (state) => state.partnerApp?.hostedEsign?.authVerify?.merchant_token_response?.token_type || 'Bearer',
    authVerifyErrMsg: (state) => state.partnerApp?.hostedEsign?.authVerify?.errMsg || null,
};

/********** ACTION_CREATORS **********/
export const actions = {
    getToken: () => ({
        type: actionTypes.getToken,
    }),
    getTokenFailed: (errMsg) => ({
        type: actionTypes.getTokenFailed,
        payload: errMsg,
    }),
    getTokenFulfilled: (access_token) => ({
        type: actionTypes.getTokenFulfilled,
        payload: access_token,
    }),

    sendAuthOtp: (data) => ({
        type: actionTypes.sendAuthOtp,
        payload: data,
    }),
    sendAuthOtpFailed: (errMsg) => ({
        type: actionTypes.sendAuthOtpFailed,
        payload: errMsg,
    }),
    sendAuthOtpFulfilled: (data) => ({
        type: actionTypes.sendAuthOtpFulfilled,
        payload: data,
    }),

    verifyAuthOtp: (data) => ({
        type: actionTypes.verifyAuthOtp,
        payload: data,
    }),
    verifyAuthOtpFailed: (errMsg) => ({
        type: actionTypes.verifyAuthOtpFailed,
        payload: errMsg,
    }),
    verifyAuthOtpFulfilled: (data) => ({
        type: actionTypes.verifyAuthOtpFulfilled,
        payload: data,
    }),

    resetState: () => ({
        type: actionTypes.resetState,
    }),
};

/********** WORKERS **********/
function* getTokenWorker() {
    try {
        const access_token = yield call(HubAuthManager.fetchHubApiToken);
        yield put(actions.getTokenFulfilled(access_token));
    } catch (err) {
        const errMsg = err?.message || "Failed to get access token";
        yield put(actions.getTokenFailed(errMsg));
    }
}

function* sendAuthOtpWorker(action) {
    const { merchant_uuid, reseller_uuid, access_token } = action.payload;
    try {
        const response = yield call(
            [axios, 'post'],
            API_ENDPOINTS.HOSTED_ESIGN.SEND_AUTH_OTP(merchant_uuid, reseller_uuid),
            {},
            {
                headers: {
                    Authorization: `Bearer ${access_token}`,
                },
            }
        );
        if (response.status === 200) {
            yield put(actions.sendAuthOtpFulfilled(response.data));
        }
    } catch (err) {
        const errMsg = err?.response?.data?.error || "Failed to send OTP";
        yield put(actions.sendAuthOtpFailed(errMsg));
    }
}

function* verifyAuthOtpWorker(action) {
    const { otp, merchant_uuid, reseller_uuid, access_token } = action.payload;
    try {
        const response = yield call(
            [axios, 'post'],
            API_ENDPOINTS.HOSTED_ESIGN.VERIFY_AUTH_OTP(merchant_uuid, reseller_uuid),
            {
                otp: String(otp)
            },
            {
                headers: {
                    Authorization: `Bearer ${access_token}`,
                },
            }
        );
        if (response.status === 200) {
            const token = response.data?.token;
            yield put(actions.verifyAuthOtpFulfilled({ token }));
        }
    } catch (err) {
        const errMsg = (err?.response?.data?.error || err?.response?.data?.message) || "Failed to verify OTP";
        yield put(actions.verifyAuthOtpFailed(errMsg));
    }
}

/********** SAGAS **********/
export function* saga() {
    yield takeLatest(actionTypes.getToken, getTokenWorker);
    yield takeLatest(actionTypes.sendAuthOtp, sendAuthOtpWorker);
    yield takeLatest(actionTypes.verifyAuthOtp, verifyAuthOtpWorker);
}